/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { Button, ButtonPrimary } from '@/components/button'
import { LoadingScreen } from '@/components/common'
import { MainContent } from '@/components/content'
import { ArrowBack, Delete, Edit, UserIconFilled } from '@/components/icons'
import {
  AutocompleteAddress,
  AutocompleteLocationInput,
  LogoInput,
  PhoneInput,
  TextArea,
  TextInput,
} from '@/components/input'
import { MainLayout } from '@/components/layouts'
import { ConfirmModal } from '@/components/modal'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { CLUB_MODIFY } from '@/constants/Common'
import { duprCountries } from '@/constants/Countries'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_CLUBS, ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { uploadImage, useGetClubStaff, useRemoveOrganizer, useSaveAddress, useUpdateClub } from '@/services'
import { ClubStaff, Country } from '@/types'
import EditClubValidationSchema from '@/utils/schema/EditClubValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { getClubDetail, getClubRoles } from '../clubSlice'
import { EditOrganizer } from '../components/EditOrganizer'
import { clubSelector } from '../selectors'

type ClubEditForm = {
  mediaId?: number
  clubName: string
  email: string
  address: AutocompleteAddress
  phone: string
  longDescription: string
}

export const ClubEdit = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { club, permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const mobileM = useMediaQuery('(max-width: 375px)')

  const { mutate: getClubStaff, data: resGetClubStaff } = useGetClubStaff()
  const { mutate: updateClubSubmit } = useUpdateClub()
  const { mutate: removeOrganizer, isLoading: isLoadingRemoveOrg } = useRemoveOrganizer()
  const { mutate: saveAddress } = useSaveAddress()

  const { clubId } = params

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false)
  const [removeOrg, setRemoveOrg] = useState<ClubStaff | null>(null)
  const [editOrg, setEditOrg] = useState<ClubStaff | null>(null)
  const [staffs, setStaffs] = useState<ClubStaff[]>([])
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    duprCountries.find((item) => item.isoCode === club?.attributes.contacts.children.isoCode?.value) || duprCountries[0]
  )

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<ClubEditForm>({
    resolver: yupResolver(EditClubValidationSchema) as any,
    mode: 'onTouched',
    defaultValues: {
      clubName: club?.clubName || '',
      email: club?.attributes.contacts.children.email?.value || '',
      address: club
        ? { description: club.address.formattedAddress, placeId: club.address.placeId }
        : { description: '', placeId: '' },
      phone:
        club?.attributes.contacts.children.phone?.value
          .split(
            duprCountries.find((item) => item.isoCode === club.attributes.contacts.children.isoCode?.value)?.dialCode ||
              duprCountries[0].dialCode
          )
          .pop() || '',
      longDescription: club?.longDescription?.content || '',
    },
  })

  const watchPhone = watch('phone')

  const handleBackToClubDetail = () => {
    navigate(`${ROUTE_CLUBS}/${clubId}`)
  }

  const onNavigateAddOrganizers = () => {
    navigate(`${ROUTE_CLUBS}/${clubId}/add-organizers`)
  }

  const onNavigateProfile = (playerId: number) => navigate(`${ROUTE_PLAYER_PROFILE}/${playerId}`)

  const onRemove = (userId: number, roleId: number) => {
    if (clubId) {
      removeOrganizer(
        {
          clubId: Number(clubId),
          userId,
          roleId,
        },
        {
          onSuccess: () => {
            toast.success('Removed')
            const filterStaffs = [...staffs].filter((item) => item.userId !== userId)
            setStaffs(filterStaffs)
            setIsRemoveModalOpen(false)
          },
          onError: (e: any) => {
            toast.error(e.response?.data.message || APP_CUSTOM_ERROR)
            setIsRemoveModalOpen(false)
          },
        }
      )
    }
  }

  const onSaveLogo = (file: File) => {
    if (club) {
      setIsLoading(true)
      uploadImage(file as File).then((res) => {
        const mediaId = res.result.id

        const reqData = {
          clubId: club?.clubId,
          mediaId: mediaId,
          attributes: club?.attributes,
        }
        updateClubSubmit(reqData, {
          onSuccess: () => {
            dispatch(getClubDetail(club.clubId.toString()))
            toast.success('Updated')
            setIsLoading(false)
          },
          onError: (error: any) => {
            toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
            setIsLoading(false)
          },
        })
      })
    }
  }

  const onRemoveLogo = () => {
    if (club) {
      setIsLoading(true)
      const reqData = {
        clubId: club.clubId,
        mediaId: -1,
        attributes: club.attributes,
      }
      updateClubSubmit(reqData, {
        onSuccess: () => {
          dispatch(getClubDetail(club.clubId.toString()))
          toast.success('Removed')
          setIsLoading(false)
        },
        onError: (error: any) => {
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          setIsLoading(false)
        },
      })
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    if (club && isValid) {
      setIsLoading(true)
      const updatedInfo = {
        clubId: club.clubId,
        clubName: data.clubName,
        longDescription: club.longDescription
          ? {
              ...club.longDescription,
              content: data.longDescription,
            }
          : {
              header: '<h1>header</h1>',
              headerType: 'text/html',
              content: data.longDescription,
              contentType: 'text/html',
              footer: '<h1>footer</h1>',
              footerType: 'text/html',
            },
        attributes: {
          contacts: {
            ...club.attributes.contacts,
            children: {
              ...club.attributes.contacts.children,
              email: {
                ...club.attributes.contacts.children.email,
                value: data.email,
              },
              phone: {
                ...club.attributes.contacts.children.phone,
                value: `${selectedCountry.dialCode}${data.phone}`,
              },
              isoCode: {
                ...club.attributes.contacts.children.isoCode,
                value: selectedCountry.isoCode,
              },
            },
          },
        },
      }

      const updateProfileOptions = {
        onSuccess: () => {
          dispatch(getClubDetail(club.clubId.toString()))
          setIsLoading(false)
          toast.success('Updated')
        },
        onError: (error: any) => {
          setIsLoading(false)
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        },
      }

      if (data.address.placeId) {
        saveAddress(
          { placeId: data.address.placeId },
          {
            onSuccess: (data) => {
              const reqData = {
                ...updatedInfo,
                addressId: data.id,
              }
              updateClubSubmit(reqData, updateProfileOptions)
            },
          }
        )
      } else {
        updateClubSubmit(updatedInfo, updateProfileOptions)
      }
    }
  })

  const onGetData = async () => {
    if (clubId && user?.id) {
      dispatch(getClubRoles({ clubId: Number(clubId), userId: user?.id }))
      dispatch(getClubDetail(clubId))
      getClubStaff(Number(clubId))
    }
  }

  useEffect(() => {
    onGetData()
  }, [clubId, user])

  useEffect(() => {
    if (club) {
      const isoCode = club.attributes.contacts.children.isoCode?.value
      reset({
        clubName: club.clubName,
        email: club.attributes.contacts.children.email?.value,
        address: {
          description: club.address.formattedAddress,
          placeId: club.address.placeId,
        },
        phone:
          club.attributes.contacts.children.phone?.value
            .split(duprCountries.find((item) => item.isoCode === isoCode)?.dialCode || duprCountries[0].dialCode)
            .pop() || '',
        longDescription: club.longDescription?.content,
      })
    }
  }, [club])

  useEffect(() => {
    if (resGetClubStaff) {
      const directors = resGetClubStaff.result.directors.map((item) => {
        return { ...item, isDirectors: true }
      })
      const organizers = resGetClubStaff.result.organizers

      setStaffs([...directors, ...organizers])
    }
  }, [resGetClubStaff])

  useEffect(() => {
    setSelectedCountry(
      duprCountries.find((item) => item.isoCode === club?.attributes.contacts.children.isoCode?.value) ||
        duprCountries[0]
    )
  }, [club?.attributes.contacts.children.isoCode?.value])

  useEffect(() => {
    if (permissions && !has(permissions, CLUB_MODIFY))
      navigate(`${ROUTE_CLUBS}/${clubId}`, { relative: 'path', replace: true })
  }, [permissions])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          {club && (
            <div className="flex flex-col gap-y-4 mb-20 lg:mb-8">
              <form onSubmit={onSubmit}>
                <div className="flex items-center justify-between">
                  <div className="flex gap-x-3 items-center">
                    <Button onClick={handleBackToClubDetail} className="hover:opacity-50">
                      <ArrowBack className="text-white" />
                    </Button>
                    <h1 className="text-sm md:text-xl text-white font-semibold">{t('club.editClub')}</h1>
                  </div>
                  {!mobileM && (
                    <ButtonPrimary
                      title="Save"
                      className="py-2 px-4"
                      type="submit"
                      disabled={
                        !isDirty && selectedCountry.isoCode === club?.attributes.contacts.children.isoCode?.value
                      }
                    />
                  )}
                </div>

                {mobileM && <ButtonPrimary title="Save" className="py-2 px-4" type="submit" />}

                <div className="flex gap-x-7 flex-col sm:flex-row">
                  <div className="w-full sm:w-[616px] flex flex-col gap-y-4">
                    <div className="pt-4 pb-2">
                      <h3 className="text-xl font-semibold text-white">{t('club.clubDetails')}</h3>
                    </div>

                    <div className="flex flex-col gap-y-2">
                      <LogoInput
                        label={t('common.logo')}
                        uploadImageModalTitle="Club Logo"
                        defaultSrc={club?.mediaUrl}
                        onSave={onSaveLogo}
                        onRemoveProfileLogo={onRemoveLogo}
                      />
                    </div>

                    <div className="flex flex-col gap-y-2">
                      <TextInput
                        placeholder={t('club.clubName')}
                        label={t('club.clubName')}
                        {...register('clubName')}
                        error={errors.clubName?.message}
                      />
                    </div>

                    <div className="flex flex-col gap-y-2">
                      <TextInput
                        placeholder={t('common.email')}
                        label={t('common.email')}
                        {...register('email')}
                        error={errors.email?.message}
                      />
                    </div>

                    <div className="flex flex-col gap-y-2">
                      <Controller
                        name="address"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteLocationInput
                            {...field}
                            label={t('common.address')}
                            setValue={(address) =>
                              setValue('address', address, { shouldDirty: true, shouldValidate: true })
                            }
                            error={errors.address?.description?.message}
                          />
                        )}
                      />
                    </div>

                    <div className="flex flex-col gap-y-2">
                      <PhoneInput
                        label={t('common.phone')}
                        placeholder={t('common.phone')}
                        maxLength={15}
                        selectedCountry={selectedCountry}
                        setSelectedCountry={setSelectedCountry}
                        phoneNumber={watchPhone}
                        setPhoneNumber={(phone: string) =>
                          setValue('phone', phone, { shouldDirty: true, shouldValidate: true })
                        }
                        error={errors.phone?.message}
                      />
                    </div>

                    <div className="flex flex-col gap-y-2">
                      <Controller
                        name="longDescription"
                        control={control}
                        render={({ field, formState }) => (
                          <TextArea
                            label={t('common.summary')}
                            placeholder={t('common.summary')}
                            defaultValue={formState.defaultValues?.longDescription}
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="w-full sm:w-[320px] flex flex-col gap-y-4">
                    <div className="flex justify-between items-center pt-4 pb-2">
                      <h3 className="text-xl font-semibold text-white">{t('club.organizers')}</h3>
                      <button
                        type="button"
                        className="text-sm text-white font-semibold rounded-full py-2 px-4 bg-primary-500"
                        onClick={onNavigateAddOrganizers}
                      >
                        {t('common.addNew')}
                      </button>
                    </div>

                    {staffs.map((item) => (
                      <div className="flex justify-between bg-white rounded-2xl p-4" key={item?.userId}>
                        <div className="flex gap-x-2 cursor-pointer" onClick={() => onNavigateProfile(item.userId)}>
                          {item?.mediaUrl ? (
                            <img src={item?.mediaUrl} className="rounded-full w-7 h-7" alt="" />
                          ) : (
                            <UserIconFilled className="w-7 h-7 text-primary-500" />
                          )}
                          <div className="flex flex-col flex-1">
                            <span className="text-sm font-semibold text-navy-900">{item?.name}</span>
                            <span className="text-sm text-navy-900 text-opacity-80 break-all">{item?.email}</span>
                            <span className="text-xs text-navy-900 text-opacity-60">{item?.phone}</span>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <button
                            type="button"
                            className="rounded-full hover:bg-navy-900 hover:bg-opacity-10 p-1.5"
                            onClick={() => setEditOrg(item)}
                          >
                            <Edit className="text-navy-900 w-5 h-5" />
                          </button>
                          {!item.isDirectors && (
                            <button
                              type="button"
                              className="rounded-full hover:bg-navy-900 hover:bg-opacity-10 p-2"
                              onClick={() => {
                                setRemoveOrg(item)
                                setIsRemoveModalOpen(true)
                              }}
                            >
                              <Delete className="text-navy-900" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            </div>
          )}
        </MainContent>
        {isLoading && <LoadingScreen />}
      </MainLayout>
      <ConfirmModal
        open={isRemoveModalOpen}
        className="!max-w-xl"
        onClose={() => {
          setRemoveOrg(null)
          setIsRemoveModalOpen(false)
        }}
        cancelTitle={t('common.goBack')}
        submitTitle={t('club.removeOrganizer')}
        disableSubmitBtn={isLoadingRemoveOrg}
        title={t('club.removeOrganizerTitle')}
        desc={t('club.removeOrganizerText')}
        onSubmit={() => {
          if (removeOrg) onRemove(removeOrg.userId, removeOrg.roleId)
        }}
      />
      <EditOrganizer
        organizer={editOrg}
        onClose={() => setEditOrg(null)}
        title={t('club.editDirectorTitle')}
        onRefresh={() => getClubStaff(Number(clubId))}
      />
    </>
  )
}
